/**
 * COL-010-C1 target-aware HUD panel placement.
 * Pure geometry: no DOM, no orbit/radial menu.
 */

export const HUD_NARROW_VIEWPORT_MAX = 720;
export const HUD_PANEL_MARGIN = 12;
export const HUD_PANEL_TOP_SAFE = 72;
export const HUD_SHEET_MAX_HEIGHT_RATIO = 0.36;

export interface PanelPoint {
  x: number;
  y: number;
}

export interface PanelBox {
  width: number;
  height: number;
}

export interface HudPanelPlacement {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
  mode: 'desktop' | 'sheet';
  targetVisible: boolean;
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) {
    return min;
  }
  return Math.min(max, Math.max(min, value));
}

function containsPoint(
  rect: { left: number; top: number; width: number; height: number },
  point: PanelPoint,
  pad: number,
): boolean {
  return (
    point.x >= rect.left - pad &&
    point.x <= rect.left + rect.width + pad &&
    point.y >= rect.top - pad &&
    point.y <= rect.top + rect.height + pad
  );
}

function finalize(
  left: number,
  top: number,
  width: number,
  height: number,
  maxHeight: number,
  viewport: PanelBox,
  margin: number,
  topSafe: number,
  mode: 'desktop' | 'sheet',
  target: PanelPoint | null,
): HudPanelPlacement {
  const maxLeft = Math.max(margin, viewport.width - width - margin);
  const maxTop = Math.max(topSafe, viewport.height - height - margin);
  const nextLeft = clamp(left, margin, maxLeft);
  const nextTop = clamp(top, topSafe, maxTop);
  return {
    left: nextLeft,
    top: nextTop,
    width,
    maxHeight,
    mode,
    targetVisible: !target || !containsPoint({ left: nextLeft, top: nextTop, width, height }, target, 8),
  };
}

export function hudPanelDragEnabled(viewportWidth: number): boolean {
  return viewportWidth > HUD_NARROW_VIEWPORT_MAX;
}

/** Keep a manually dragged panel fully inside the visible viewport and below the top safe area. */
export function clampHudPanelDragOffset(
  base: { left: number; top: number; width: number; height: number },
  offset: PanelPoint,
  viewport: PanelBox,
  margin = HUD_PANEL_MARGIN,
  topSafe = HUD_PANEL_TOP_SAFE,
): PanelPoint {
  const minX = margin - base.left;
  const maxX = viewport.width - margin - (base.left + base.width);
  const minY = topSafe - base.top;
  const maxY = viewport.height - margin - (base.top + base.height);
  return {
    x: clamp(offset.x, Math.min(minX, maxX), Math.max(minX, maxX)),
    y: clamp(offset.y, Math.min(minY, maxY), Math.max(minY, maxY)),
  };
}

/** Viewport-safe panel box that stays off the active target when practical. */
export function placeHudPanel(input: {
  viewport: PanelBox;
  target: PanelPoint | null;
  panel: PanelBox;
  margin?: number;
  topSafe?: number;
}): HudPanelPlacement {
  const margin = input.margin ?? HUD_PANEL_MARGIN;
  const topSafe = input.topSafe ?? HUD_PANEL_TOP_SAFE;
  const viewport = input.viewport;
  const narrow = viewport.width <= HUD_NARROW_VIEWPORT_MAX;
  const width = Math.min(input.panel.width, Math.max(1, viewport.width - margin * 2));
  const target = input.target;

  if (narrow) {
    const maxHeight = Math.min(input.panel.height, Math.round(viewport.height * HUD_SHEET_MAX_HEIGHT_RATIO));
    const height = Math.min(input.panel.height, Math.max(48, maxHeight));
    const sheetWidth = Math.max(1, viewport.width - margin * 2);
    let top = viewport.height - height - margin;
    if (target && containsPoint({ left: margin, top, width: sheetWidth, height }, target, 16)) {
      const above = target.y - height - 16;
      const below = target.y + 16;
      if (above >= topSafe) {
        top = above;
      } else if (below + height <= viewport.height - margin) {
        top = below;
      }
    }
    return finalize(margin, top, sheetWidth, height, maxHeight, viewport, margin, topSafe, 'sheet', target);
  }

  const maxHeight = Math.min(input.panel.height, Math.max(48, viewport.height - topSafe - margin));
  const height = Math.min(input.panel.height, maxHeight);
  let left = (viewport.width - width) / 2;
  let top = viewport.height - height - margin;

  if (target) {
    const preferAbove = target.y >= viewport.height * 0.45;
    const preferRight = target.x < viewport.width * 0.5;
    top = preferAbove ? target.y - height - 20 : target.y + 20;
    if (top < topSafe || top + height > viewport.height - margin) {
      left = preferRight ? target.x + 24 : target.x - width - 24;
    }
    const current = { left, top, width, height };
    if (containsPoint(current, target, 12)) {
      left = preferRight ? target.x + 24 : target.x - width - 24;
    }
    if (containsPoint({ left, top, width, height }, target, 12)) {
      top = preferAbove ? target.y + 20 : target.y - height - 20;
    }
  }

  return finalize(left, top, width, height, maxHeight, viewport, margin, topSafe, 'desktop', target);
}
